/**
 * API Service Layer — Centralized data fetching with error handling
 *
 * This module abstracts all API calls behind a typed interface.
 * Currently uses mock data from engagementData.ts; replace fetch implementations
 * when backend is ready.
 *
 * Usage:
 *   const engagements = await api.engagements.getByFirm('firm-us');
 *   const tasks = await api.tasks.getAll('eng-alphago');
 */



import {
  msalInstance,
  msalInitPromise,
  apiTokenRequest,
  getAccessToken,
  isInteractionRequiredError,
  redirectToReAuthenticate,
} from '../auth/msalConfig';
import { environment } from '../environments/environment';

// ---- Configuration ---- //

const API_CONFIG = {
  baseUrl: import.meta.env.VITE_apiBaseUrl || '/api',
  timeout: 30_000,
} as const;

type ApiRequestOptions = RequestInit & {
  timeoutMs?: number;
};

const AUTH_PROTECTED_API_URLS = [
  import.meta.env.VITE_apiBaseUrl,
  import.meta.env.VITE_apiServiceUrl,
  import.meta.env.VITE_apiResourceBase,
  import.meta.env.VITE_kciGatewayUrl,
].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

function resolveOrigin(url: string): string | null {
  try {
    return new URL(url, window.location.origin).origin;
  } catch {
    return null;
  }
}

const AUTH_PROTECTED_ORIGINS = new Set(
  AUTH_PROTECTED_API_URLS
    .map((url) => resolveOrigin(url))
    .filter((origin): origin is string => origin !== null)
);

// ---- Error Types ---- //

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(message: string, status = 0, code = 'UNKNOWN_ERROR') {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

export class NetworkError extends ApiError {
  constructor(message = 'Network request failed') {
    super(message, 0, 'NETWORK_ERROR');
    this.name = 'NetworkError';
  }
}

export class TimeoutError extends ApiError {
  constructor(message = 'Request timed out') {
    super(message, 408, 'TIMEOUT');
    this.name = 'TimeoutError';
  }
}

function buildAiIntegrationHeaders(): Record<string, string> {
  const integrationId =
    environment.aiIntegrationId?.trim()
    || (import.meta.env.VITE_aiintegrationId as string | undefined)?.trim()
    || '';

  if (!integrationId) {
    return {};
  }

  // Keep both header keys for backward compatibility across services.
  return {
    aiintegrationid: integrationId,
    'aiintegration-id': integrationId,
  };
}

// ---- Generic Fetch Wrapper ---- //

async function apiFetch<T>(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), options.timeoutMs ?? API_CONFIG.timeout);

  try {
    const requestUrl = /^https?:\/\//i.test(endpoint)
      ? endpoint
      : `${API_CONFIG.baseUrl}${endpoint}`;

    const resolvedRequestUrl = new URL(requestUrl, window.location.origin);
    const shouldAttachAuth = AUTH_PROTECTED_ORIGINS.has(resolvedRequestUrl.origin);

    let authHeaders: Record<string, string> = {};

    if (shouldAttachAuth) {
      try {
        await msalInitPromise;
        const activeAccount =
          msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
        if (activeAccount) {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            ...apiTokenRequest,
            account: activeAccount,
          });
          authHeaders = { Authorization: `Bearer ${tokenResponse.accessToken}` };
        } else {
          console.warn(
            '[apiFetch] No MSAL account available; skipping Authorization header for',
            resolvedRequestUrl.origin
          );
        }
      } catch (error) {
        if (isInteractionRequiredError(error)) {
          console.warn('[apiFetch] Refresh token is dead; redirecting to interactive login.', {
            origin: resolvedRequestUrl.origin,
            error,
          });
          await redirectToReAuthenticate();
        } else {
          console.warn(
            '[apiFetch] Failed to acquire API bearer token; request will be sent without Authorization header.',
            {
              origin: resolvedRequestUrl.origin,
              scopes: apiTokenRequest.scopes,
              error,
            }
          );
        }
        // Allow unauthenticated local calls to proceed; protected APIs will reject if auth is required.
      }
    }

    const response = await fetch(requestUrl, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...buildAiIntegrationHeaders(),
        ...authHeaders,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const responseBody = await response.text().catch(() => '');
      const details = responseBody.trim();
      throw new ApiError(
        details
          ? `API error: ${response.statusText}. Response: ${details}`
          : `API error: ${response.statusText}`,
        response.status,
        `HTTP_${response.status}`
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return await response.json() as T;
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new TimeoutError();
    }
    throw new NetworkError(
      error instanceof Error ? error.message : 'Unknown network error'
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

async function apiFetchBlob(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<Blob> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), options.timeoutMs ?? API_CONFIG.timeout);

  try {
    const requestUrl = /^https?:\/\//i.test(endpoint)
      ? endpoint
      : `${API_CONFIG.baseUrl}${endpoint}`;

    const resolvedRequestUrl = new URL(requestUrl, window.location.origin);
    const shouldAttachAuth = AUTH_PROTECTED_ORIGINS.has(resolvedRequestUrl.origin);

    let authHeaders: Record<string, string> = {};

    if (shouldAttachAuth) {
      try {
        const accessToken = await getAccessToken();
        if (accessToken) {
          authHeaders = { Authorization: `Bearer ${accessToken}` };
        }
      } catch {
        // Allow unauthenticated local calls to proceed.
      }
    }

    const response = await fetch(requestUrl, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        ...buildAiIntegrationHeaders(),
        ...authHeaders,
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new ApiError(
        `API error: ${response.statusText}`,
        response.status,
        `HTTP_${response.status}`
      );
    }

    return await response.blob();
  } catch (error) {
    if (error instanceof ApiError) throw error;
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new TimeoutError();
    }
    throw new NetworkError(
      error instanceof Error ? error.message : 'Unknown network error'
    );
  } finally {
    clearTimeout(timeoutId);
  }
}


// ---- Unified API Object ---- //

export const api = { 
  /** Low-level fetch wrapper for custom endpoints */
  fetch: apiFetch,
  /** Low-level fetch wrapper that returns a Blob (for file downloads) */
  fetchBlob: apiFetchBlob,
} as const;

export default api;